import { styled } from '@mui/material/styles';
import { groupBy, sortBy } from 'lodash';
import { GhgScopeClobDto } from 'models/dtoTypes';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import { greenHouseGasSvg } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import Empty from './_empty';

const PREFIX = 'GreenHouseGasReport';

const classes = {
  item: `${PREFIX}-item`,
  scope: `${PREFIX}-scope`,
  container: `${PREFIX}-container`,
};

const Scope = styled('div')(() => ({
  [`& .${classes.scope}`]: {
    width: '100%',
    paddingLeft: 30,
  },
  [`& .${classes.container}`]: {
    padding: 0,
    paddingLeft: 10,
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: 'white',
  },
  [`& .${classes.item}`]: {
    width: '100%',
    padding: 10,
    '&:first-of-type': {
      paddingTop: 0,
    },
    '&:last-child': {
      paddingBottom: 0,
    }
  },
}));

interface IProps {
  name?: string;
  ghgScopeReport: GhgScopeClobDto[];
}

export default function GreenHouseGasReport(props: IProps): JSX.Element {
  const {
    name = 'Activity check against Greenhouse Gases (GHG), Scope one, two and three. ',
    ghgScopeReport,
  } = props;

  const renderScope = (title: string, items: GhgScopeClobDto[], color: string) => {
    return (
      <Scope key={title}>
        <h4>{title}</h4>
        <div className={classes.scope} style={{ backgroundColor: color }}>
          <div className={classes.container}>
            {items.map((item, index) => (
              <div key={index} className={classes.item}>
                <SdgScoreClob
                  weight="regular"
                  key={index}
                  withBg={true}
                  title={item.title || ''}
                  affinity={item.affinity}
                />
              </div>
            ))}
          </div>
        </div>
      </Scope>
    );
  };

  const renderContent = () => {
    if (ghgScopeReport.length === 0) {
      return <Empty title="There is no data available"/>;
    }

    const scopes = groupBy(ghgScopeReport, item => item.scope);
    const colors = ['#e4b02f', '#c5bc69', '#80d0c7'];

    return (
      <div>
        {Object.keys(scopes).map((scope, index) => {
          const items = sortBy(scopes[scope], item => item.subScope);
          return renderScope(`Scope ${scope}`, items, colors[index % 3]);
        })}
      </div>
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: 'Negative activity found towards corresponding scope.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: 'Signs of both positive and negative activity found towards corresponding scope, but mostly negative.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'positive',
        title: 'Positive activity found towards corresponding scope.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'neutral',
        title: 'Signs of balanced positive and negative activity found towards corresponding scope.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'unscored',
        title: 'Not applicable to this company, or not enough information available.',
        withBg: true,
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={name}
        learnMoreTabId="ghg"
      />
    );
  };

  return (
    <SubSection
      title="Greenhouse Gases"
      isBold
      icon={<img src={greenHouseGasSvg} />}
    >
      <>
        {renderLegands()}
        {renderContent()}
      </>
    </SubSection>
  );
}

